import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { alpha, styled } from "@mui/material/styles";
import { Button, TablePagination, Typography, TextField } from "@mui/material";
import Grid from "@mui/material/Grid";
import moment from "moment";
import Modal from "../Modal";
import Toast from "../../../utils/toast";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import createInstance from "../../../api/axiosinstance";

const aiDishaApi = createInstance("DISHA");

const CssTextField = styled(TextField)(({ theme }) => ({
  "& label.Mui-focused": {
    color: "white",
  },
  "& label": {
    color: "white",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "white",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "white",
      borderRadius: 10,
    },
    "&:hover fieldset": {
      borderColor: "white",
    },
    "&.Mui-focused fieldset": {
      borderColor: "white",
    },
    "& .MuiInputBase-input": {
      color: "white",
    },
  },
}));

const StateManagement = () => {
  const [states, setStates] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stateIdToChange, setStateIdToChange] = useState("");
  const [actionType, setActionType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [stateData, setStatesData] = useState([]);

  useEffect(() => {
    fetchActiveStates();
    fetchStatesTable();
  }, []);

  const fetchActiveStates = async () => {
    try {
      const response = await aiDishaApi.get("/get_states_dropdown");
      console.log("Fetched states for dropdown:", response.data);
      setStates(response.data);
    } catch (error) {
      console.error("Error fetching active states:", error);
      Toast.error("Failed to fetch states for dropdown");
    }
  };

  const fetchStatesTable = async () => {
    try {
      const response = await aiDishaApi.get("/get-all-states");
      console.log("Fetched states for table:", response.data);
      setStatesData(response.data);
    } catch (error) {
      console.error("Error fetching states for table:", error);
      Toast.error("Failed to fetch states for table");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalOpen = (stateId, action) => {
    setStateIdToChange(stateId);
    setActionType(action);
    setIsModalOpen(true);
  };

  const handleConfirm = async () => {
    try {
      const status = actionType === "activate" ? "Active" : "Inactive";
      await aiDishaApi.patch(
        `/states/${stateIdToChange}/active-inactive?status=${status}`
      );
      Toast.success(`State ${status.toLowerCase()}d successfully`);
      await fetchActiveStates(); // Refresh dropdown
      await fetchStatesTable(); // Refresh table
    } catch (error) {
      console.error(`Error changing state status:`, error);
      Toast.error(`Failed to ${actionType} state`);
    } finally {
      setIsModalOpen(false);
    }
  };

  const handleDropdownChange = (e) => {
    const selected = e.target.value;
    setSelectedState(selected);
    setStateCode(states[selected] || "");
  };

  const handleClear = () => {
    setSelectedState("");
    setStateCode("");
    setSearchTerm("");
  };

  const handleStateSubmit = async () => {
    if (!selectedState || !selectedState.trim()) {
      Toast.error("State name cannot be empty");
      return;
    }
    try {
      const response = await aiDishaApi.post(
        `/add-states/?state_name=${encodeURIComponent(selectedState)}`
      );
      if (response.status === 200) {
        Toast.success("State submitted successfully");
        handleClear();
        await fetchActiveStates(); // Refresh dropdown
        await fetchStatesTable(); // Refresh table
      }
    } catch (error) {
      console.error("Error while submitting state:", error);
      Toast.error(
        error.response?.data?.detail || "Failed to submit state data"
      );
    }
  };

  const roles = [
    {
      id: "State ID",
      name: "State Name",
      code: "State Code",
      createdDate: "Creation Date",
      action: "Action",
    },
  ];

  // Use stateData for table rows instead of states
  const rows = Array.isArray(stateData)
    ? stateData.map((state) => ({
        state_id: state.state_id,
        state_name: state.state_name,
        state_code: state.state_code,
        created_date: state.created_date
          ? moment(state.created_date).format("DD/MM/YYYY")
          : "N/A",
        status: state.status,
      }))
    : [];

  const filteredRows = rows.filter((row) =>
    row.state_name?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  const paginatedRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div>
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        sx={{
          marginTop: "30px",
          alignItems: "center",
          background: alpha("#033538", 1),
          borderRadius: "10px",
          padding: "15px",
        }}
      >
        <Box gridColumn="span 6" sx={{ marginTop: "auto", textAlign: "left" }}>
          <Typography
            sx={{ fontFamily: "poppins", fontSize: "28px", color: "white" }}
          >
            Add State
          </Typography>
          <Typography
            sx={{ fontFamily: "poppins", fontSize: "16px", color: "white" }}
          >
            Add the Name of the State
          </Typography>
        </Box>
        <Box
          gridColumn="span 6"
          sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}
        >
          <Paper
            sx={{
              p: "2px 4px",
              display: "flex",
              width: "75%",
              borderRadius: 2,
              backgroundColor: "#006A71",
            }}
          >
            <IconButton
              type="button"
              sx={{ p: "8px", color: "white" }}
              aria-label="search"
            >
              <SearchIcon />
            </IconButton>
            <InputBase
              sx={{ ml: 1, flex: 1, fontFamily: "Poppins", color: "white" }}
              placeholder="Search by State Name"
              inputProps={{ "aria-label": "search google maps" }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Paper>
        </Box>

        <Box gridColumn="span 4" sx={{ marginTop: "auto", textAlign: "left" }}>
          <Typography sx={{ fontFamily: "poppins", color: "white" }}>
            State Name
          </Typography>
          <div className="col-sm-12">
            <select
              name="state"
              className="form-select form-select-sm"
              aria-label="Small select example"
              style={{
                backgroundColor: "#033538",
                color: "white",
                border: "1px solid white",
                width: "95%",
                "--bs-form-select-bg-img": `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='white'><path d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592c.86 0 1.319 1.013.753 1.658L8.753 11.14a1 1 0 0 1-1.506 0z'/></svg>")`,

              }}
              value={selectedState}
              onChange={handleDropdownChange}
            >
              <option value="" disabled>
                Select State
              </option>
              {Object.keys(states).length > 0 ? (
                Object.keys(states).map((stateName, index) => (
                  <option key={index} value={stateName} >
                    {stateName}
                  </option>
                ))
              ) : (
                <option value="">No states available</option>
              )}
            </select>
          </div>
        </Box>

        <Box gridColumn="span 4" sx={{ marginTop: "auto", textAlign: "left" }}>
          <Typography sx={{ fontFamily: "poppins", color: "white" }}>
            State Code
          </Typography>
          <CssTextField
            disabled
            label="State Code"
            id="custom-css-outlined-input"
            value={stateCode}
            size="small"
            sx={{
              width: "95%",

              /* ✅ outline color */
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "white",
    },

    /* ✅ keep outline white on hover */
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "white",
    },

    /* ✅ focused outline */
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "white",
    },

    /* ✅ disabled input text */
    "& .MuiInputBase-input.Mui-disabled": {
      color: "white",
      WebkitTextFillColor: "white",
    },

    /* ✅ disabled outline */
    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: "white",
    },
            }}
            InputLabelProps={{
              style: { color: "white",
                 "&.Mui-focused": {
        color: "white",
      },
      "&.Mui-disabled": {
        color: "white",
      },
               },
            }}
          />
        </Box>

        <Box
          gridColumn="span 2"
          sx={{ marginTop: "auto", ml: "15%", textAlign: "left" }}
        >
          <Button
            onClick={handleClear}
            sx={{
              border: "1px solid white",
              color: "white",
              width: "120px",
              height: "40px",
              borderRadius: "10px",
              fontFamily: "poppins",
            }}
          >
            Clear
          </Button>
        </Box>

        <Box gridColumn="span 2" sx={{ marginTop: "auto", textAlign: "left" }}>
          <Button
            onClick={handleStateSubmit}
            sx={{
              background: "#006A71",
              width: "120px",
              height: "40px",
              borderRadius: "10px",
              fontFamily: "poppins",
            }}
          >
            <Typography sx={{ fontFamily: "poppins", color: "white" }}>
              Save
            </Typography>
          </Button>
        </Box>
      </Box>

      <Box xs={1} sx={{ backgroundColor: "#33333" }}>
        <Grid container spacing={1} sx={{ justifyContent: "center" }}>
          <Grid item xs={10}>
            <TableContainer
              component={Paper}
              sx={{
                marginTop: "25px",
                borderRadius: "10px",
              }}
            >
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#006A71" }}>
                  {roles.map((state) => (
                    <TableRow key={state.id}>
                      <TableCell sx={{ color: "white", borderBottom: 0 }}>
                        {state.id}
                      </TableCell>
                      <TableCell sx={{ color: "white", borderBottom: 0 }}>
                        {state.name}
                      </TableCell>
                      <TableCell sx={{ color: "white", borderBottom: 0 }}>
                        {state.code}
                      </TableCell>
                      <TableCell sx={{ color: "white", borderBottom: 0 }}>
                        {state.createdDate}
                      </TableCell>
                      <TableCell sx={{ color: "white", borderBottom: 0 }}>
                        {state.action}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableHead>
                <TableBody>
                  {paginatedRows.length > 0 ? (
                    paginatedRows.map((row) => (
                      <TableRow
                        key={row.state_id}
                        sx={{ backgroundColor: "#033538", marginRight: "40px" }}
                      >
                        <TableCell
                          sx={{ color: "white", border: "1px solid black" }}
                        >
                          {row.state_id}
                        </TableCell>
                        <TableCell
                          sx={{ color: "white", border: "1px solid black" }}
                        >
                          {row.state_name}
                        </TableCell>
                        <TableCell
                          sx={{ color: "white", border: "1px solid black" }}
                        >
                          {row.state_code}
                        </TableCell>
                        <TableCell
                          sx={{ color: "white", border: "1px solid black" }}
                        >
                          {row.created_date}
                        </TableCell>
                        <TableCell
                          sx={{ color: "white", border: "1px solid black" }}
                        >
                          {row.status === "Active" ? (
                            <Button
                              variant="contained"
                              sx={{
                                color: "white",
                                marginLeft: "20px",
                                fontFamily: "poppins",
                                height: "20px",
                                width: "90px",
                                backgroundColor: "red",
                              }}
                              onClick={() =>
                                handleModalOpen(row.state_id, "deactivate")
                              }
                            >
                              Deactivate
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              sx={{
                                color: "white",
                                marginLeft: "20px",
                                fontFamily: "poppins",
                                height: "20px",
                                width: "90px",
                                backgroundColor: "green",
                              }}
                              onClick={() =>
                                handleModalOpen(row.state_id, "activate")
                              }
                            >
                              Activate
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{ color: "white", textAlign: "center" }}
                      >
                        No Data Available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  backgroundColor: "#033538",
                  color: "#B9B9B9",
                }}
              />
            </TableContainer>
          </Grid>
        </Grid>
      </Box>

      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title={
          actionType === "deactivate"
            ? "Confirm Deactivation"
            : "Confirm Activation"
        }
        action={handleConfirm}
      >
        <Typography>
          Are you sure you want to{" "}
          {actionType === "deactivate" ? "deactivate" : "activate"} this state?
        </Typography>
      </Modal>
    </div>
  );
};

export default StateManagement;